import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Plus, UserPlus, Trash2, Mail, Phone, RefreshCw, RotateCcw, Download, FileText, CheckCircle, MessageCircle, Briefcase, Moon, ClipboardCheck, ArrowRight, ShieldCheck, AlertTriangle, Link2, XCircle } from 'lucide-react';
import CameraCapture from './CameraCapture';
import { useApp } from '../context/AppContext';
import VsureCheckModal from './VsureCheckModal';

// Shared Applicants register — interview / new-hire intake. Used by both the
// engine Personnel page and the mobile driver portal (DriverLogPage). Reads and
// writes the same Apps Script actions (saveApplicant / getApplicants /
// deleteApplicant), so an applicant recorded in the portal shows up in the
// engine and vice-versa, photos and documents included.

// Applicants + onboarding now run on Supabase (applicants-sb); was the appsscript proxy.
const APPLICANTS_URL = '/.netlify/functions/applicants-sb';

const labelStyle = {
  fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)',
  textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5,
};
const inputStyle = { color: 'var(--hp-text)', background: 'var(--hp-surface)' };

function SectionTitle({ n, title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, marginTop: n > 1 ? 20 : 0 }}>
      <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--hp-gold)', color: '#000', fontSize: '.65rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</div>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--hp-gold)', fontWeight: 700 }}>{title}</span>
    </div>
  );
}

const EMPTY_APPLICANT = { fullName: '', mobile: '', email: '', dateOfBirth: '', notes: '', photo: '', usesWhatsApp: false };

// The tick-box sections Paul can request back from an applicant. Keys match the
// backend INFO_SECTIONS + the public /provide-info form.
const INFO_SECTION_LIST = [
  ['photo_id',          'Photo ID'],
  ['work_rights',       'Work rights / visa'],
  ['tfn_super_bank',    'TFN, super & bank'],
  ['contact_emergency', 'Contact & emergency details'],
  ['dob',               'Date of birth'],
];
const INFO_SECTION_LABEL = Object.fromEntries(INFO_SECTION_LIST);

// Photo ID the office already holds — a licence scanned at interview, or one sent back on
// an info request. It moves to the driver's compliance file on hire, so don't ask again.
const PHOTO_ID_KINDS = [
  ["Driver's licence", ["Driver's Licence — Front", "Driver's Licence — Back"]],
  ['Photo ID', ['Photo ID — Front', 'Photo ID — Back']],
];
function photoIdOnFile(applicant) {
  const have = new Set((Array.isArray(applicant.documents) ? applicant.documents : []).map(d => d && d.label));
  const hits = PHOTO_ID_KINDS
    .map(([name, [front, back]]) => (have.has(front) ? `${name} (front${have.has(back) ? ' + back' : ''})` : ''))
    .filter(Boolean);
  return hits.length ? hits.join(', ') : '';
}

// Friendly labels for the answers an applicant returns via the RFI form.
const INFO_ANSWER_ROWS = [
  ['fullName', 'Name given'],
  ['dateOfBirth', 'Date of birth'],
  ['mobile', 'Mobile'],
  ['email', 'Email'],
  ['currentAddress', 'Current address'],
  ['citizen', 'Citizen'],
  ['citizenProof', 'Proof of citizenship'],
  ['country', 'Country'],
  ['visaType', 'Visa type'],
  ['grantNumber', 'Visa grant no.'],
  ['passportNumber', 'Passport no.'],
  ['passportExpiry', 'Passport expiry'],
  ['hasLicence', 'AU driver’s licence'],
  ['tfn', 'TFN'],
  ['superFund', 'Super fund'],
  ['superMember', 'Super member no.'],
  ['accountName', 'Account name'],
  ['bsb', 'BSB'],
  ['accNumber', 'Account number'],
  ['emergencyName', 'Emergency contact'],
  ['emergencyPhone', 'Emergency phone'],
  ['emergencyRelationship', 'Emergency relationship'],
];

// Default document-capture slots. All optional; "Other" is free-labelled and
// more can be added.
function defaultDocSlots() {
  return [
    { id: 'licence_front', label: "Driver's Licence — Front", photo: '', required: false, fixedLabel: true },
    { id: 'licence_back',  label: "Driver's Licence — Back",  photo: '', required: false, fixedLabel: true },
    { id: 'other',         label: 'Other Document (e.g. Visa, Medicare)', photo: '', required: false, fixedLabel: false },
  ];
}

function AddApplicantPanel({ onClose, onSaved, addNotification, recordedBy }) {
  const [form, setForm] = useState(EMPTY_APPLICANT);
  const [docs, setDocs] = useState(defaultDocSlots);
  const [saving, setSaving] = useState(false);
  const docSeq = useRef(0);
  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const setDoc = (id, patch) => setDocs(prev => prev.map(d => d.id === id ? { ...d, ...patch } : d));
  const addDoc = () => { docSeq.current += 1; setDocs(prev => [...prev, { id: 'doc_' + docSeq.current, label: '', photo: '', required: false, fixedLabel: false, removable: true }]); };
  const removeDoc = (id) => setDocs(prev => prev.filter(d => d.id !== id));

  const b64 = (dataUrl) => (dataUrl && dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl);

  const handleSubmit = async () => {
    if (!form.fullName.trim()) { addNotification('Full name is required', 'error'); return; }
    if (!form.mobile.trim())   { addNotification('Mobile number is required', 'error'); return; }
    const unlabeled = docs.find(d => d.photo && !d.label.trim());
    if (unlabeled) { addNotification('Please name every document you have captured', 'error'); return; }

    const documents = docs
      .filter(d => d.photo)
      .map(d => ({ label: d.label.trim() || 'Document', photoBase64: b64(d.photo), contentType: 'image/jpeg' }));

    setSaving(true);
    try {
      const res = await fetch(APPLICANTS_URL, {
        method: 'POST',
        body: JSON.stringify({
          action: 'saveApplicant',
          fullName: form.fullName.trim(),
          mobile: form.mobile.trim(),
          email: form.email.trim(),
          dateOfBirth: form.dateOfBirth || '',
          notes: form.notes.trim(),
          recordedBy: recordedBy || '',
          usesWhatsApp: form.usesWhatsApp,
          photoBase64: b64(form.photo),
          contentType: 'image/jpeg',
          documents,
        }),
      });
      const data = await res.json();
      if (data.success) {
        addNotification(`Applicant ${form.fullName.trim()} recorded`, 'success');
        onSaved();
        onClose();
      } else {
        addNotification(`Failed: ${data.error}`, 'error');
      }
    } catch (err) {
      addNotification(`Error: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Opaque full-screen backdrop + opaque panel: when this form is handed to an
  // applicant to fill in, NOTHING behind it (other applicants' photos/data) is
  // visible. --hp-bg is undefined app-wide, so use defined opaque vars.
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--hp-black)', zIndex: 3000, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ width: 480, maxWidth: '100%', background: 'var(--hp-card)', borderLeft: '1px solid var(--hp-border)', height: '100%', overflowY: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--hp-gold)', marginBottom: 4 }}>Personnel · Applicants</div>
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Record Applicant</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <SectionTitle n={1} title="Applicant Details" />
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Full Name *</label>
          <input className="form-input" style={inputStyle} placeholder="Exactly as shown on passport" value={form.fullName} onChange={e => set('fullName', e.target.value)} />
          <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', marginTop: 3 }}>As what is on your passport</div>
        </div>
        <div style={{ marginBottom: 8 }}>
          <label style={labelStyle}>Mobile Number *</label>
          <input className="form-input" style={inputStyle} type="tel" inputMode="tel" placeholder="04xx xxx xxx" value={form.mobile} onChange={e => set('mobile', e.target.value)} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer', fontSize: '.82rem', color: 'var(--hp-text)' }}>
            <input type="checkbox" checked={form.usesWhatsApp} onChange={e => set('usesWhatsApp', e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--hp-green)' }} />
            This number uses WhatsApp
          </label>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Email Address</label>
          <input className="form-input" style={inputStyle} type="email" inputMode="email" placeholder="name@example.com" value={form.email} onChange={e => set('email', e.target.value)} />
          <div style={{ fontSize: '.65rem', color: 'var(--hp-dim)', marginTop: 3 }}>Optional — a mobile number is enough to text them a request.</div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Date of Birth</label>
          <input className="form-input" style={inputStyle} type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} />
        </div>
        <div style={{ marginBottom: 4 }}>
          <label style={labelStyle}>Notes</label>
          <textarea className="form-input" style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} placeholder="Optional — interview notes, role, availability…" value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>

        <SectionTitle n={2} title="Photos — Device Camera" />
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Passport Photo — Head &amp; Shoulders</label>
          <CameraCapture value={form.photo} onCapture={url => set('photo', url)} onClear={() => set('photo', '')} guide="portrait" />
        </div>

        {docs.map(d => (
          <div key={d.id} style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              {d.fixedLabel ? (
                <label style={{ ...labelStyle, marginBottom: 0 }}>{d.label}{d.required ? ' *' : ''}</label>
              ) : (
                <input
                  className="form-input"
                  style={{ ...inputStyle, fontSize: '.8rem', padding: '5px 8px' }}
                  placeholder="Document name (e.g. Visa, Medicare)"
                  value={d.label}
                  onChange={e => setDoc(d.id, { label: e.target.value })}
                />
              )}
              {d.removable && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeDoc(d.id)} title="Remove document"><X size={14} /></button>
              )}
            </div>
            <CameraCapture value={d.photo} onCapture={url => setDoc(d.id, { photo: url })} onClear={() => setDoc(d.id, { photo: '' })} guide="document" maxEdge={1400} quality={0.85} />
          </div>
        ))}

        <button type="button" className="btn btn-secondary btn-sm" onClick={addDoc}>
          <Plus size={13} /> Add additional document
        </button>

        <div style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 16, marginTop: 24, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Save Applicant'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Convert an AU mobile (04xx…) to the wa.me international form (61…), digits only.
function waNumber(mobile) {
  let d = (mobile || '').replace(/[^\d]/g, '');
  if (d.startsWith('0')) d = '61' + d.slice(1);
  else if (!d.startsWith('61') && d.length === 9) d = '61' + d;
  return d;
}

// A single checklist row: a checkbox that toggles a lifecycle flag, with an
// optional action button (e.g. open WhatsApp / email) alongside.
function CheckRow({ label, checked, onToggle, sub, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flex: 1 }}>
        <input type="checkbox" checked={!!checked} onChange={e => onToggle(e.target.checked)} style={{ width: 17, height: 17, accentColor: 'var(--hp-green)', flexShrink: 0 }} />
        <span>
          <span style={{ fontSize: '.86rem', color: 'var(--hp-text)', fontWeight: checked ? 600 : 400 }}>{label}</span>
          {sub && <span style={{ display: 'block', fontSize: '.68rem', color: 'var(--hp-dim)' }}>{sub}</span>}
        </span>
      </label>
      {action}
    </div>
  );
}

// Download the ORIGINAL stored image (not the on-screen thumbnail) so it can be
// re-uploaded to the AusPost portal at full resolution — a screenshot downscales
// and fails their 480×640 minimum. Fetch as a blob so the file saves with a
// sensible name regardless of how applicant-doc sets Content-Disposition.
const dlName = (s) => ((s || 'applicant').toString().replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'applicant');

async function downloadImage(url, filename, addNotification) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`server returned ${res.status}`);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
  } catch (err) {
    if (addNotification) addNotification(`Could not download the photo: ${err.message}`, 'error');
  }
}

function DownloadButton({ url, filename, addNotification, label = 'Download' }) {
  const [busy, setBusy] = useState(false);
  if (!url) return null;
  return (
    <button
      type="button"
      className="btn btn-secondary btn-sm"
      style={{ marginTop: 8 }}
      disabled={busy}
      onClick={async () => { setBusy(true); await downloadImage(url, filename, addNotification); setBusy(false); }}
    >
      <Download size={12} /> {busy ? 'Downloading…' : label}
    </button>
  );
}

// Fix a wrong photo on a submitted applicant record (e.g. they uploaded their
// passport photo instead of their ID), or add a document to one that has none.
// Collapsed to a small button; tapping it reveals CameraCapture and the captured
// image is posted to replaceApplicantDoc — the other documents are left untouched.
// In `isNew` mode it also collects a label so a fresh document can be named.
function PhotoReplacer({ applicantId, target, label, fileId, isNew, onReplaced, addNotification }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newLabel, setNewLabel] = useState(label || 'Photo ID');
  const upload = async (dataUrl) => {
    if (!dataUrl) return;
    const sendLabel = isNew ? (newLabel.trim() || 'Document') : label;
    const comma = dataUrl.indexOf(',');
    const contentType = (dataUrl.slice(5, comma).split(';')[0]) || 'image/jpeg';
    const photoBase64 = dataUrl.slice(comma + 1);
    setBusy(true);
    try {
      const res = await fetch(APPLICANTS_URL, {
        method: 'POST',
        body: JSON.stringify({ action: 'replaceApplicantDoc', id: applicantId, target, label: sendLabel, fileId, photoBase64, contentType }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'replace failed');
      addNotification(isNew ? 'Document added' : 'Photo replaced', 'success');
      setOpen(false);
      if (data.applicant && onReplaced) onReplaced(data.applicant);
    } catch (err) {
      addNotification(`Could not ${isNew ? 'add document' : 'replace photo'}: ${err.message}`, 'error');
    } finally { setBusy(false); }
  };
  if (!open) {
    return (
      <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={() => setOpen(true)}>
        {isNew ? <><Plus size={12} /> Add a document</> : <><RotateCcw size={12} /> Retake / replace</>}
      </button>
    );
  }
  return (
    <div style={{ marginTop: 8 }}>
      {isNew && (
        <input
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          placeholder="Document name (e.g. Photo ID — Front)"
          style={{ ...inputStyle, width: '100%', padding: '9px 10px', borderRadius: 6, border: '1px solid var(--hp-border)', fontSize: '.8rem', marginBottom: 8, boxSizing: 'border-box' }}
        />
      )}
      <CameraCapture nativeFirst guide={target === 'headshot' ? 'portrait' : 'document'} maxEdge={1600} quality={0.85} value={null} onCapture={upload} onClear={() => {}} />
      <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={() => setOpen(false)}>
        {busy ? 'Uploading…' : 'Cancel'}
      </button>
    </div>
  );
}

// Work rights before hire — a VEVO check through vSure, once the applicant has consented
// on their info-request form. The result follows them into Compliance when they're hired.
const VEVO_RIGHT = {
  unlimited:       { label: 'Unlimited work rights', color: 'var(--hp-green)', bg: 'rgba(22,163,74,.12)' },
  '48h_fortnight': { label: '48h / fortnight cap',   color: '#b8860b',         bg: 'rgba(201,168,76,.16)' },
  no_work:         { label: 'No work rights',        color: '#dc2626',         bg: 'rgba(220,38,38,.12)' },
  unknown:         { label: 'Needs review',          color: 'var(--hp-dim)',   bg: 'var(--hp-surface)' },
};
const DMS_URL = '/.netlify/functions/driver-dms-sb';
const dmsCall = async (payload) => (await fetch(DMS_URL, { method: 'POST', body: JSON.stringify(payload) })).json();
const fmtAU = (d) => (d ? new Date(d.length === 10 ? d + 'T00:00:00' : d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

function ApplicantVevoPanel({ applicant, addNotification, onChanged }) {
  const [checking, setChecking] = useState(false);
  const v = applicant.vevo || null;
  const consent = !!(v && v.consent && v.consent.given);
  const c = v && v.check;
  const answers = (applicant.infoRequest && applicant.infoRequest.data) || {};
  const ob = applicant.onboarding || {};
  const citizen = (answers.citizen || ob.citizen) === 'yes';
  const r = c ? (VEVO_RIGHT[c.workRight] || VEVO_RIGHT.unknown) : null;
  const openPdf = async () => {
    const w = window.open('', '_blank');
    try {
      const d = await dmsCall({ action: 'getApplicantVevoFile', applicantId: applicant.id });
      if (!d || !d.success) throw new Error(d?.error || 'Could not open the VEVO result');
      const bytes = Uint8Array.from(atob(d.base64), (x) => x.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: d.contentType || 'application/pdf' }));
      if (w) w.location.href = url; setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (w) w.close(); addNotification(e.message, 'error'); }
  };
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ ...labelStyle, marginBottom: 8 }}>Work rights (VEVO)</div>
      <div style={{ padding: '10px 12px', background: 'var(--hp-surface)', borderRadius: 8, fontSize: '.8rem', color: 'var(--hp-text)', display: 'grid', gap: 6 }}>
        {c ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 800, color: r.color, background: r.bg, borderRadius: 999, padding: '3px 10px' }}>
                <ShieldCheck size={13} /> {r.label}
              </span>
              {c.hasFile && <button className="btn btn-ghost btn-sm" onClick={openPdf}><FileText size={13} /> VEVO {fmtAU(c.checkedOn)}</button>}
            </div>
            <div style={{ color: 'var(--hp-dim)' }}>
              Subclass {c.subclass || '—'}{c.visaName ? ` (${c.visaName})` : ''} · expires {c.noExpiry ? 'never' : fmtAU(c.expiryDate)} · conditions {c.conditions.join(', ') || 'none'}
            </div>
            <div style={{ color: 'var(--hp-dim)', fontSize: '.74rem' }}>{c.workBasis}</div>
          </>
        ) : citizen ? (
          <div style={{ color: 'var(--hp-dim)' }}>Says they're an Australian citizen / permanent resident. VEVO can't check Australian passports, so check their passport, citizenship or birth certificate instead.</div>
        ) : consent ? (
          <div style={{ color: 'var(--hp-dim)' }}>Consent given {fmtAU(v.consent.at)}, not checked yet.</div>
        ) : (
          <div style={{ color: 'var(--hp-dim)' }}>No consent yet. The <strong>onboarding link</strong> below asks a non-citizen for their passport and consent. To ask on its own, send the info request with <strong>Work rights / visa</strong> ticked.</div>
        )}
        {v && v.lastAttempt && (
          <div style={{ color: '#dc2626', fontSize: '.76rem', display: 'flex', gap: 5 }}><AlertTriangle size={13} /> Last check {fmtAU(v.lastAttempt.at)}: {v.lastAttempt.text}</div>
        )}
        {v && v.carriedTo && <div style={{ color: 'var(--hp-dim)', fontSize: '.74rem' }}>Copied to {v.carriedTo}'s compliance file on hire.</div>}
        {consent && !citizen && (
          <div><button className="btn btn-sm" onClick={() => setChecking(true)} style={{ borderColor: 'var(--hp-green)', color: 'var(--hp-green)' }}>
            <ShieldCheck size={13} /> {c ? 'Re-check with vSure' : v.pending ? 'vSure check pending…' : 'Check with vSure'}
          </button></div>
        )}
      </div>
      {checking && (
        <VsureCheckModal name={applicant.fullName || 'Applicant'} consentLabel="Consent given on their form"
          load={() => dmsCall({ action: 'applicantVsurePrepare', applicantId: applicant.id })}
          run={(p) => dmsCall({ action: 'applicantVsureCheck', applicantId: applicant.id, ...p })}
          viewScan={(prep) => prep.scanPath ? { url: `/.netlify/functions/applicant-doc?path=${encodeURIComponent(prep.scanPath)}` } : null}
          onClose={() => setChecking(false)}
          onSaved={async () => { setChecking(false); addNotification('VEVO check saved.', 'success'); if (onChanged) await onChanged(); }}
          onError={(m) => addNotification(m, 'error')} />
      )}
    </div>
  );
}

// DD.MM.YYYY from an ISO date — the format Paul copies into AP / DVS forms.
const fmtDots = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || ''); return m ? `${m[3]}.${m[2]}.${m[1]}` : ''; };

// Licence number / card number / expiry read off the licence photos by Claude, shown as
// text with a Copy each so nobody has to open the image to key them in. Reads on its
// own the first time the card opens (and again when a licence photo is replaced).
function LicenceDetails({ applicant, onReplaced }) {
  const lic = applicant.licence;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState('');
  const tried = useRef('');
  const read = useCallback(async () => {
    setBusy(true); setMsg('');
    try {
      const d = await (await fetch(APPLICANTS_URL, { method: 'POST', body: JSON.stringify({ action: 'readApplicantLicence', id: applicant.id }) })).json();
      if (d.success && d.applicant) onReplaced && onReplaced(d.applicant);
      else setMsg(d.error || 'Could not read the licence');
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  }, [applicant.id, onReplaced]);
  const staleKey = lic && lic.stale ? `${applicant.id}|${(applicant.documents || []).map(d => d.fileId).join('|')}` : '';
  useEffect(() => {
    if (staleKey && tried.current !== staleKey) { tried.current = staleKey; read(); }
  }, [staleKey, read]);
  if (!lic) return null;
  const copy = async (k, v) => { try { await navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(''), 1500); } catch {} };
  const expired = lic.expiryDate && lic.expiryDate < new Date().toISOString().slice(0, 10);
  const rows = [
    ['num', 'Licence no.', lic.licenceNumber],
    ['card', 'Card no.', lic.cardNumber],
    ['exp', 'Expiry', fmtDots(lic.expiryDate)],
  ];
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ ...labelStyle, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Driver's licence</span>
        <button className="btn btn-ghost btn-sm" style={{ fontSize: '.66rem', padding: '2px 6px' }} disabled={busy} onClick={read} title="Read the licence photos again">
          <RefreshCw size={11} /> {busy ? 'Reading…' : 'Re-read'}
        </button>
      </div>
      {busy && !lic.read ? (
        <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>Reading the licence photos…</div>
      ) : (
        <div style={{ background: 'var(--hp-surface)', borderRadius: 8, padding: '4px 12px' }}>
          {rows.map(([k, label, v]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: k === 'exp' ? 'none' : '1px solid var(--hp-border)' }}>
              <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)', width: 74, flexShrink: 0 }}>{label}</span>
              <span style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: '.92rem', userSelect: 'all', color: k === 'exp' && expired ? 'var(--hp-red)' : 'var(--hp-text)' }}>
                {v || '—'}{k === 'exp' && expired ? ' (expired)' : ''}
              </span>
              {v && <button className="btn btn-secondary btn-sm" style={{ fontSize: '.66rem', padding: '3px 8px' }} onClick={() => copy(k, v)}>{copied === k ? 'Copied' : 'Copy'}</button>}
            </div>
          ))}
        </div>
      )}
      {(msg || lic.error) && !busy && <div style={{ fontSize: '.72rem', color: 'var(--hp-red)', marginTop: 6 }}>{msg || lic.error}</div>}
      {lic.read && !lic.error && !busy && <div style={{ fontSize: '.66rem', color: 'var(--hp-dim)', marginTop: 4 }}>Read from the photos by Claude — check against the image before relying on it.</div>}
    </div>
  );
}

function ApplicantDetailModal({ applicant, onClose, onDelete, deleting, onPatch, onHire, onHireSorter, onStartOnboarding, onSendInfo, onMarkReviewed, onReplaced, onVevoChanged, addNotification }) {
  const fullPhoto = applicant.photoThumb ? applicant.photoThumb.replace('sz=w600', 'sz=w1600') : applicant.photoUrl;
  const when = applicant.createdAt ? new Date(applicant.createdAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  const hired = applicant.status === 'Hired' || !!applicant.hiredAt;
  // Not hired = we've decided against them. Kept on file (not deleted) so the card,
  // photo and ID stay for reference; Undo puts them back to a plain applicant.
  const notHired = !hired && applicant.status === 'Not hired';
  // An applicant can be hired into either register: contractor Drivers or the PAYG
  // night sorters. Rows hired before hiredAs existed are all drivers.
  const asSorter = applicant.hiredAs === 'sorter' || (!applicant.hiredAs && !applicant.driverLogin && !!applicant.sorterLogin);
  const hiredRegister = asSorter ? 'Night Sorters' : 'Drivers';
  const hiredLogin = asSorter ? (applicant.sorterLogin || '') : (applicant.driverLogin || '');
  const firstName = (applicant.fullName || '').trim().split(/\s+/)[0] || 'there';
  const guideUrl = 'https://hollypaul.com/docs/onboarding-guide-en.pdf';
  const nextStepsMsg = `Hi ${firstName}, thanks for meeting with Hollypaul. Here is your Next Steps guide — it walks you through everything from the police / Fit2Work check to your first deliveries:\n\n${guideUrl}\n\nAny questions, just reply here.`;
  const waHref = `https://wa.me/${waNumber(applicant.mobile)}?text=${encodeURIComponent(nextStepsMsg)}`;
  const mailHref = `mailto:${applicant.email}?subject=${encodeURIComponent('Hollypaul — Next Steps')}&body=${encodeURIComponent(nextStepsMsg)}`;
  const linkBtn = { fontSize: '.72rem', padding: '4px 10px', textDecoration: 'none', whiteSpace: 'nowrap' };
  const hiredWhen = applicant.hiredAt ? new Date(applicant.hiredAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  // Has onboarding already been started for this person? Only asked when the host
  // page offers the control at all — the driver portal's copy of this view doesn't,
  // and must not call an admin endpoint.
  const [onbTask, setOnbTask] = useState(undefined);   // undefined = not looked up yet
  const [onbBusy, setOnbBusy] = useState(false);
  useEffect(() => {
    if (!onStartOnboarding || !applicant.id) { setOnbTask(null); return; }
    let live = true;
    fetch('/.netlify/functions/onboarding-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'findTask', applicantId: applicant.id }),
    })
      .then((r) => r.json())
      .then((d) => { if (live) setOnbTask(d && d.success ? (d.task || null) : null); })
      .catch(() => { if (live) setOnbTask(null); });
    return () => { live = false; };
  }, [applicant.id, onStartOnboarding]);

  // ── Self-service onboarding ──
  const ob = applicant.onboarding || {};
  const obSubmitted = !!applicant.onboardingSubmittedAt;
  const [obLink, setObLink] = useState(applicant.onboardingToken ? `https://hollypaul.com/onboard?token=${applicant.onboardingToken}` : '');
  const [obBusy, setObBusy] = useState('');
  const [obMsg, setObMsg] = useState('');
  const [obCopied, setObCopied] = useState(false);
  const obPost = async (action) => { const r = await fetch(APPLICANTS_URL, { method: 'POST', body: JSON.stringify({ action, applicantId: applicant.id }) }); return r.json(); };
  const genLink = async () => {
    setObBusy('link'); setObMsg('');
    try { const d = await obPost('createOnboardingToken'); if (d.success) { setObLink(d.link); try { await navigator.clipboard.writeText(d.link); setObCopied(true); setTimeout(() => setObCopied(false), 2000); } catch {} } else setObMsg(d.error || 'Could not generate link'); }
    finally { setObBusy(''); }
  };
  const copyLink = async () => { try { await navigator.clipboard.writeText(obLink); setObCopied(true); setTimeout(() => setObCopied(false), 2000); } catch {} };
  const emailLink = async () => {
    setObBusy('email'); setObMsg('');
    try { const d = await obPost('sendOnboardingLink'); if (d.success) { setObMsg(`✓ Emailed to ${d.sentTo}`); if (d.link) setObLink(d.link); } else setObMsg(d.error || 'Could not send'); }
    finally { setObBusy(''); }
  };

  const idOnFile = photoIdOnFile(applicant);

  // ── Request for Information (SMS) ──
  const ir = applicant.infoRequest || null;
  const irSubmitted = !!(ir && ir.status === 'submitted');
  const [pick, setPick] = useState(() => new Set(ir && Array.isArray(ir.sections) ? ir.sections : []));
  const [irNote, setIrNote] = useState((ir && ir.note) || '');
  const [irBusy, setIrBusy] = useState(false);
  const [irMsg, setIrMsg] = useState('');
  const [irLink, setIrLink] = useState('');
  const togglePick = (k) => setPick(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const sendInfo = async () => {
    if (!pick.size) { setIrMsg('Tick at least one thing to request.'); return; }
    setIrBusy(true); setIrMsg('');
    try {
      const d = await onSendInfo(applicant.id, Array.from(pick), irNote.trim());
      if (d && d.success) {
        setIrLink(d.link || '');
        setIrMsg(d.skipped
          ? 'Link created — SMS is not switched on yet, so copy the link and send it yourself.'
          : `✓ Texted to ${d.sentTo}`);
      } else setIrMsg((d && d.error) || 'Could not send the request.');
    } catch (e) { setIrMsg(e.message); }
    finally { setIrBusy(false); }
  };
  // Opening a submitted-but-unreviewed applicant clears the red badge.
  useEffect(() => {
    if (irSubmitted && ir && !ir.reviewedAt && onMarkReviewed) onMarkReviewed(applicant.id);
  }, [applicant.id, irSubmitted]); // eslint-disable-line

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3500, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 50, overflowY: 'auto' }}>
      <div style={{ width: 460, maxWidth: '94%', background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 12, overflow: 'hidden', marginBottom: 40 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--hp-border)' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--hp-gold)' }}>Applicant</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: 20 }}>
          {hired && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', marginBottom: 14, background: 'rgba(22,163,74,.12)', border: '1px solid rgba(22,163,74,.4)', borderRadius: 8, color: 'var(--hp-green)', fontSize: '.82rem', fontWeight: 600 }}>
              <CheckCircle size={16} />
              Loaded into system — added to {hiredRegister}{hiredLogin ? ` as ${hiredLogin}` : ''}{hiredWhen ? ` · ${hiredWhen}` : ''}
            </div>
          )}
          {fullPhoto ? (
            <a href={applicant.photoUrl} target="_blank" rel="noopener noreferrer">
              <img src={fullPhoto} alt={applicant.fullName} style={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', borderRadius: 10, border: '1px solid var(--hp-border)', maxHeight: 380 }} />
            </a>
          ) : (
            <div style={{ width: '100%', aspectRatio: '3 / 4', maxHeight: 380, background: 'var(--hp-surface)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--hp-dim)', fontSize: '.8rem' }}>No photo</div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <DownloadButton url={applicant.photoUrl} filename={`${dlName(applicant.fullName)}-photo.jpg`} addNotification={addNotification} label="Download photo" />
            <PhotoReplacer applicantId={applicant.id} target="headshot" onReplaced={onReplaced} addNotification={addNotification} />
          </div>
          <h3 style={{ margin: '16px 0 4px', fontSize: '1.15rem' }}>{applicant.fullName || '—'}</h3>
          <span className={`badge ${hired ? 'badge-green' : notHired ? 'badge-red' : 'badge-blue'}`} style={{ marginBottom: 12, display: 'inline-block' }}>{hired ? `Hired${hiredWhen ? ` · ${hiredWhen}` : ''}` : (applicant.status || 'Applicant')}</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            <a href={`tel:${applicant.mobile}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.88rem', color: 'var(--hp-text)', textDecoration: 'none' }}>
              <Phone size={14} color="var(--hp-gold)" /> {applicant.mobile || '—'}
            </a>
            <a href={`mailto:${applicant.email}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.88rem', color: 'var(--hp-text)', textDecoration: 'none', wordBreak: 'break-all' }}>
              <Mail size={14} color="var(--hp-gold)" /> {applicant.email || '—'}
            </a>
            {applicant.dateOfBirth && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.88rem', color: 'var(--hp-text)' }}>
                <span style={{ fontSize: '.7rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', width: 14, textAlign: 'center' }}>DOB</span>
                {new Date(applicant.dateOfBirth).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            )}
          </div>
          {applicant.notes && (
            <div style={{ marginTop: 14, padding: '10px 12px', background: 'var(--hp-surface)', borderRadius: 8, fontSize: '.82rem', color: 'var(--hp-text)', whiteSpace: 'pre-wrap' }}>{applicant.notes}</div>
          )}

          <ApplicantVevoPanel applicant={applicant} addNotification={addNotification} onChanged={onVevoChanged} />

          <LicenceDetails applicant={applicant} onReplaced={onReplaced} />

          {(() => {
            const docs = Array.isArray(applicant.documents) ? applicant.documents : [];
            return (
            <div style={{ marginTop: 16 }}>
              <div style={{ ...labelStyle, marginBottom: 8 }}>Documents ({docs.length})</div>
              {docs.length === 0 && (
                <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginBottom: 8 }}>No documents on file yet.</div>
              )}
              <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                {docs.map((doc, i) => {
                  const full = doc.thumbUrl ? doc.thumbUrl.replace('sz=w600', 'sz=w1600') : doc.viewUrl;
                  return (
                    <div key={i}>
                      <a href={doc.viewUrl || full} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                        <div style={{ width: '100%', aspectRatio: '4 / 3', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {full
                            ? <img src={doc.thumbUrl || full} alt={doc.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.currentTarget.style.display = 'none'; }} />
                            : <span style={{ fontSize: '.72rem', color: 'var(--hp-dim)' }}>No image</span>}
                        </div>
                        <div style={{ fontSize: '.72rem', color: 'var(--hp-text)', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.label || `Document ${i + 1}`}</div>
                      </a>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <DownloadButton url={doc.viewUrl || full} filename={`${dlName(applicant.fullName)}-${dlName(doc.label || `document-${i + 1}`)}.jpg`} addNotification={addNotification} />
                        <PhotoReplacer applicantId={applicant.id} label={doc.label || `Document ${i + 1}`} fileId={doc.fileId} onReplaced={onReplaced} addNotification={addNotification} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <PhotoReplacer applicantId={applicant.id} isNew onReplaced={onReplaced} addNotification={addNotification} />
            </div>
            );
          })()}

          {/* Self-service onboarding */}
          <div style={{ marginTop: 18, borderTop: '1px solid var(--hp-border)', paddingTop: 12 }}>
            <div style={{ ...labelStyle, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Self-service onboarding</span>
              {obSubmitted
                ? <span className="badge badge-green" style={{ fontSize: '.6rem' }}>Submitted</span>
                : <span className="badge badge-blue" style={{ fontSize: '.6rem' }}>{obLink ? 'Link generated' : 'Not started'}</span>}
            </div>

            {!obSubmitted && (
              <>
                <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 8, lineHeight: 1.45 }}>
                  {idOnFile
                    ? <><span style={{ color: 'var(--hp-green)', fontWeight: 600 }}>✓ Photo ID on file: {idOnFile}.</span> The form doesn't ask for it again, and it moves to their compliance file when you activate them.</>
                    : 'No photo ID on file. Add their licence under Documents above, or tick Photo ID in the request below.'}
                  {' '}If they aren't a citizen, the form asks for their passport and VEVO consent.
                </div>
                {obLink ? (
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    <input readOnly value={obLink} onFocus={e => e.target.select()} style={{ flex: 1, fontSize: '.72rem', padding: '7px 9px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)' }} />
                    <button className="btn btn-secondary btn-sm" onClick={copyLink}>{obCopied ? 'Copied' : 'Copy'}</button>
                  </div>
                ) : (
                  <button className="btn btn-primary btn-sm" style={{ width: '100%', marginBottom: 8 }} disabled={!!obBusy} onClick={genLink}>{obBusy === 'link' ? 'Generating…' : 'Get onboarding link'}</button>
                )}
                {obLink && (
                  <button className="btn btn-secondary btn-sm" style={{ width: '100%' }} disabled={!!obBusy || !applicant.email} onClick={emailLink}>
                    <Mail size={12} /> {obBusy === 'email' ? 'Sending…' : (applicant.email ? `Email link to ${applicant.email}` : 'No email on file')}
                  </button>
                )}
                {obMsg && <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 6 }}>{obMsg}</div>}
              </>
            )}

            {obSubmitted && (
              <div>
                <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginBottom: 8 }}>Completed {new Date(applicant.onboardingSubmittedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} — check the details + documents above, then activate.</div>
                <table style={{ width: '100%', fontSize: '.8rem', borderCollapse: 'collapse' }}><tbody>
                  {[
                    ['Preferred name', ob.preferredName],
                    ['Current address', ob.sameAddress === false ? ob.currentAddress : 'Same as licence'],
                    ['Emergency contact', [ob.emergencyName, ob.emergencyPhone, ob.emergencyRelationship].filter(Boolean).join(' · ')],
                    ['Citizen / resident', ob.citizen === 'yes' ? 'Yes' : ob.citizen === 'no' ? `No — ${[ob.country, ob.visaType, ob.grantNumber && ('grant ' + ob.grantNumber)].filter(Boolean).join(', ')}` : '—'],
                    ['ACN', ob.acn],
                    ['GST registered', ob.gstRegistered],
                    ['Workers comp', [ob.workersCompPolicy, ob.workersCompExpiry && ('exp ' + ob.workersCompExpiry)].filter(Boolean).join(' · ')],
                    ['Account name', ob.accountName],
                    ['BSB / Account', [ob.bsb, ob.accNumber].filter(Boolean).join(' / ')],
                  ].map(([k, v]) => (
                    <tr key={k} style={{ borderBottom: '1px solid var(--hp-border)' }}>
                      <td style={{ padding: '5px 10px 5px 0', color: 'var(--hp-dim)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
                      <td style={{ padding: '5px 0', color: 'var(--hp-text)', textAlign: 'right' }}>{v || '—'}</td>
                    </tr>
                  ))}
                </tbody></table>
                {!hired && !notHired && (onHire || onHireSorter) && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    {onHire && (
                      <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => onHire(applicant)}>
                        <Briefcase size={14} /> Activate as Driver
                      </button>
                    )}
                    {onHireSorter && (
                      <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => onHireSorter(applicant)}>
                        <Moon size={14} /> Add to Night Sorters
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Request for Information (SMS) */}
          <div style={{ marginTop: 18, borderTop: '1px solid var(--hp-border)', paddingTop: 12 }}>
            <div style={{ ...labelStyle, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Request information</span>
              {irSubmitted
                ? <span className="badge badge-green" style={{ fontSize: '.6rem' }}>Returned</span>
                : ir && ir.sentAt
                  ? <span className="badge badge-blue" style={{ fontSize: '.6rem' }}>Sent — awaiting</span>
                  : <span className="badge" style={{ fontSize: '.6rem', background: 'var(--hp-surface)', color: 'var(--hp-dim)' }}>Not sent</span>}
            </div>

            {!irSubmitted && (
              <>
                <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                  Tick what {(applicant.fullName || 'they').split(' ')[0]} needs to send back, then text them a secure link to complete it on their phone.
                </div>
                <div style={{ marginBottom: 10 }}>
                  {INFO_SECTION_LIST.map(([k, lbl]) => {
                    const held = k === 'photo_id' ? idOnFile : '';
                    return (
                      <label key={k} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '5px 0', cursor: 'pointer' }}>
                        <input type="checkbox" checked={pick.has(k)} onChange={() => togglePick(k)} style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--hp-green)', flexShrink: 0 }} />
                        <span style={{ fontSize: '.84rem', color: 'var(--hp-text)', fontWeight: pick.has(k) ? 600 : 400 }}>
                          {held ? (pick.has(k) ? 'Photo ID: ask for another one' : 'Photo ID') : lbl}
                          {held && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.72rem', fontWeight: 400, color: 'var(--hp-green)', marginTop: 2 }}>
                              <CheckCircle size={12} /> On file: {held}{pick.has(k) ? '. Kept, and they add a second ID.' : '. Tick only if you need another form of ID.'}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <textarea
                  className="form-input"
                  style={{ ...inputStyle, minHeight: 44, resize: 'vertical', fontSize: '.8rem', marginBottom: 8 }}
                  placeholder="Optional message to include (e.g. why you're asking)…"
                  value={irNote}
                  onChange={e => setIrNote(e.target.value)}
                />
                <button
                  className="btn btn-primary btn-sm"
                  style={{ width: '100%' }}
                  disabled={irBusy || !applicant.mobile || pick.size === 0}
                  onClick={sendInfo}
                >
                  <MessageCircle size={12} /> {irBusy ? 'Sending…' : (ir && ir.sentAt ? `Re-send to ${applicant.mobile}` : (applicant.mobile ? `Text request to ${applicant.mobile}` : 'No mobile on file'))}
                </button>
                {ir && ir.sentAt && !irMsg && (
                  <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginTop: 6 }}>
                    Last sent {new Date(ir.sentAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} to {ir.sentTo || applicant.mobile}
                  </div>
                )}
                {irLink && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <input readOnly value={irLink} onFocus={e => e.target.select()} style={{ flex: 1, fontSize: '.72rem', padding: '7px 9px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)' }} />
                    <button className="btn btn-secondary btn-sm" onClick={() => { try { navigator.clipboard.writeText(irLink); } catch {} }}>Copy</button>
                  </div>
                )}
                {irMsg && <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 6 }}>{irMsg}</div>}
              </>
            )}

            {irSubmitted && (
              <div>
                <div style={{ fontSize: '.7rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                  {ir.via === 'apply-link' ? 'Applied online' : 'Returned'} {ir.submittedAt ? new Date(ir.submittedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  {ir.via !== 'apply-link' && Array.isArray(ir.sections) && ir.sections.length ? ` · asked for ${ir.sections.map(k => INFO_SECTION_LABEL[k] || k).join(', ')}` : ''}. Any documents are in the set above.
                </div>
                <table style={{ width: '100%', fontSize: '.8rem', borderCollapse: 'collapse' }}><tbody>
                  {INFO_ANSWER_ROWS
                    .map(([k, lbl]) => [lbl, (ir.data || {})[k]])
                    .filter(([, v]) => v)
                    .map(([k, v]) => (
                      <tr key={k} style={{ borderBottom: '1px solid var(--hp-border)' }}>
                        <td style={{ padding: '5px 10px 5px 0', color: 'var(--hp-dim)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
                        <td style={{ padding: '5px 0', color: 'var(--hp-text)', textAlign: 'right', wordBreak: 'break-word' }}>{String(v)}</td>
                      </tr>
                    ))}
                </tbody></table>
                <button className="btn btn-secondary btn-sm" style={{ width: '100%', marginTop: 10 }} disabled={irBusy} onClick={sendInfo}>
                  <MessageCircle size={12} /> Request more / re-send
                </button>
                {irMsg && <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 6 }}>{irMsg}</div>}
              </div>
            )}
          </div>

          {/* Onboarding / Next Steps checklist */}
          {onPatch && (
            <div style={{ marginTop: 18, borderTop: '1px solid var(--hp-border)', paddingTop: 12 }}>
              <div style={{ ...labelStyle, marginBottom: 2 }}>Next Steps</div>
              <CheckRow
                label="Loaded into MMT"
                sub="Triggers the Fit2work email to the applicant"
                checked={!!applicant.mmtLoadedAt}
                onToggle={v => onPatch({ mmtLoaded: v })}
              />
              <CheckRow
                label="Next steps sent — Email"
                checked={!!applicant.nextStepsEmailAt}
                onToggle={v => onPatch({ nextStepsEmail: v })}
                action={applicant.email ? (
                  <a className="btn btn-secondary btn-sm" style={linkBtn} href={mailHref}><Mail size={12} /> Email</a>
                ) : null}
              />
              <CheckRow
                label="Next steps sent — WhatsApp"
                sub={applicant.usesWhatsApp ? null : 'Applicant didn’t confirm WhatsApp at interview'}
                checked={!!applicant.nextStepsWhatsappAt}
                onToggle={v => onPatch({ nextStepsWhatsapp: v })}
                action={applicant.usesWhatsApp && applicant.mobile ? (
                  <a className="btn btn-secondary btn-sm" style={linkBtn} href={waHref} target="_blank" rel="noopener noreferrer"><MessageCircle size={12} /> WhatsApp</a>
                ) : null}
              />
              {!hired && (
                <button className="btn btn-secondary btn-sm" style={{ width: '100%', marginTop: 12, color: notHired ? undefined : 'var(--hp-red)' }}
                  onClick={() => onPatch({ status: notHired ? 'Applicant' : 'Not hired' })}>
                  {notHired ? <><RotateCcw size={12} /> Undo — back to applicant</> : <><XCircle size={12} /> Mark not hired</>}
                </button>
              )}
              {!hired && !notHired && !obSubmitted && (onHire || onHireSorter) && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  {onHire && (
                    <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => onHire(applicant)}>
                      <Briefcase size={14} /> Hire — Drivers
                    </button>
                  )}
                  {onHireSorter && (
                    <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => onHireSorter(applicant)}>
                      <Moon size={14} /> Hire — Night Sorters
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Bring them on — hands this person to whoever runs the eight steps.
              Available before hiring on purpose: creating the driver record IS step 1.
              Named apart from "Self-service onboarding" above, which is the form the
              APPLICANT fills in on their phone. This one is the office's own work. */}
          {onStartOnboarding && (
            <div style={{ marginTop: 18, borderTop: '1px solid var(--hp-border)', paddingTop: 14 }}>
              <div style={{ ...labelStyle, marginBottom: 8 }}>Bring them on</div>
              {onbTask ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="badge badge-green" style={{ fontSize: '.6rem' }}>Started</span>
                  <span style={{ fontSize: '.76rem', color: 'var(--hp-dim)', flex: 1, minWidth: 150 }}>
                    Assigned to {onbTask.assignedTo}
                    {onbTask.status === 'open' ? ' — not accepted yet' : onbTask.status === 'done' ? ' — closed' : ' — in progress'}
                  </span>
                  <button className="btn btn-secondary btn-sm" onClick={() => onStartOnboarding(applicant, onbTask)}>
                    Open the task <ArrowRight size={12} />
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
                    Our side of it, not theirs: sets the eight-step task — login and PIN, documents,
                    prices, streets, compliance, vehicle, lease, subcontractor agreement — and hands
                    it to Jayda to accept.
                  </div>
                  <button className="btn btn-primary" style={{ width: '100%' }} disabled={onbBusy || onbTask === undefined}
                    onClick={async () => { setOnbBusy(true); try { await onStartOnboarding(applicant, null); } finally { setOnbBusy(false); } }}>
                    <ClipboardCheck size={14} /> {onbBusy ? 'Starting…' : 'Start the onboarding process'}
                  </button>
                </>
              )}
            </div>
          )}

          <div style={{ marginTop: 14, fontSize: '.7rem', color: 'var(--hp-dim)' }}>
            {when && <>Recorded {when}</>}{applicant.recordedBy ? ` · by ${applicant.recordedBy}` : ''}
          </div>
          <div style={{ borderTop: '1px solid var(--hp-border)', marginTop: 16, paddingTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--hp-danger)' }} onClick={onDelete} disabled={deleting}>
              <Trash2 size={13} /> {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ApplicantsView({ addNotification, recordedBy, onHire, onHireSorter, onStartOnboarding, initialApplicantId, reloadSignal }) {
  const { refreshApplicantInfoAlerts } = useApp();
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // Arrived from an onboarding task ("from the applicant record") — open that person
  // once the list has loaded. Fires once, so closing the modal doesn't reopen it.
  const openedInitial = useRef(false);
  useEffect(() => {
    if (openedInitial.current || !initialApplicantId || !applicants.length) return;
    const hit = applicants.find((a) => a.id === initialApplicantId);
    if (hit) { openedInitial.current = true; setSelected(hit); }
  }, [initialApplicantId, applicants]);

  // NOTE: addNotification is intentionally NOT a dependency. It can be recreated
  // on every render of the parent provider, so depending on it here would make
  // `load` change identity, re-fire the mount effect, and (on an error →
  // notification → re-render) spin into an infinite fetch loop. Its behaviour is
  // stable, so closing over the first instance is fine.
  const loadReq = useRef(0);
  const load = useCallback(async () => {
    const myReq = ++loadReq.current;
    setLoading(true);
    try {
      const res = await fetch(APPLICANTS_URL, { method: 'POST', body: JSON.stringify({ action: 'getApplicants' }) });
      const data = await res.json();
      if (myReq !== loadReq.current) return; // a newer load started — drop this stale response
      if (data.success) {
        const list = data.applicants || [];
        setApplicants(list);
        // Keep the open detail modal in sync with the freshly loaded data.
        setSelected(prev => (prev ? (list.find(a => a.id === prev.id) || prev) : prev));
      } else {
        addNotification(`Could not load applicants: ${data.error}`, 'error');
      }
    } catch (err) {
      if (myReq === loadReq.current) addNotification(`Error loading applicants: ${err.message}`, 'error');
    } finally {
      if (myReq === loadReq.current) setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  // Reload when the parent signals (e.g. after a hire completes in the engine).
  useEffect(() => { if (reloadSignal) load(); }, [reloadSignal]); // eslint-disable-line

  // Map a lifecycle patch to the local fields getApplicants returns, for an
  // optimistic UI update (so a ticked box stays ticked without a full reload).
  const optimistic = (patch) => {
    const nowIso = new Date().toISOString();
    const o = {};
    if (patch.mmtLoaded         !== undefined) o.mmtLoadedAt = patch.mmtLoaded ? nowIso : '';
    if (patch.nextStepsEmail    !== undefined) o.nextStepsEmailAt = patch.nextStepsEmail ? nowIso : '';
    if (patch.nextStepsWhatsapp !== undefined) o.nextStepsWhatsappAt = patch.nextStepsWhatsapp ? nowIso : '';
    if (patch.usesWhatsApp      !== undefined) o.usesWhatsApp = !!patch.usesWhatsApp;
    if (patch.status            !== undefined) o.status = patch.status;
    return o;
  };

  const patchApplicant = async (id, patch) => {
    const o = optimistic(patch);
    // Snapshot just the fields we're about to change, so a failure reverts ONLY
    // those (using load() to roll back has the same network failure mode offline).
    const prevA = applicants.find(a => a.id === id);
    const revert = {};
    Object.keys(o).forEach(k => { revert[k] = prevA ? prevA[k] : undefined; });
    setApplicants(prev => prev.map(a => a.id === id ? { ...a, ...o } : a));
    setSelected(prev => (prev && prev.id === id ? { ...prev, ...o } : prev));
    try {
      const res = await fetch(APPLICANTS_URL, { method: 'POST', body: JSON.stringify({ action: 'saveApplicant', id, ...patch }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'save failed');
    } catch (err) {
      setApplicants(prev => prev.map(a => a.id === id ? { ...a, ...revert } : a));
      setSelected(prev => (prev && prev.id === id ? { ...prev, ...revert } : prev));
      addNotification(`Change not saved${navigator.onLine ? '' : ' — you appear to be offline'}: ${err.message}`, 'error');
    }
  };

  // Ticked sections → mint token + SMS the applicant. Returns the raw result so the
  // modal can show the link / status; refreshes the list on success.
  const sendInfoRequest = async (id, sections, note) => {
    try {
      const res = await fetch(APPLICANTS_URL, { method: 'POST', body: JSON.stringify({ action: 'sendInfoRequest', applicantId: id, sections, note }) });
      const data = await res.json();
      if (data.success) { addNotification(data.skipped ? 'Request link created (SMS not enabled yet)' : 'Request texted to applicant', 'success'); load(); }
      return data;
    } catch (err) { return { success: false, error: err.message }; }
  };

  // Opening a returned request clears the red badge (optimistic + server + nav count).
  const markInfoReviewed = async (id) => {
    const stamp = new Date().toISOString();
    setApplicants(prev => prev.map(a => (a.id === id && a.infoRequest) ? { ...a, infoRequest: { ...a.infoRequest, reviewedAt: stamp } } : a));
    setSelected(prev => (prev && prev.id === id && prev.infoRequest) ? { ...prev, infoRequest: { ...prev.infoRequest, reviewedAt: stamp } } : prev);
    try {
      await fetch(APPLICANTS_URL, { method: 'POST', body: JSON.stringify({ action: 'markInfoReviewed', applicantId: id }) });
      if (refreshApplicantInfoAlerts) refreshApplicantInfoAlerts();
    } catch { /* the periodic poll will reconcile */ }
  };

  // The standing "apply remotely" link — one link for anyone who can't come in.
  // Copies on tap; New link retires the old one (if it was posted somewhere it shouldn't be).
  const [applyLink, setApplyLink] = useState('');
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyCopied, setApplyCopied] = useState(false);
  const copyApplyLink = async (regenerate) => {
    if (regenerate && !window.confirm('Make a new apply link? The old link will stop working for anyone who hasn’t used it yet.')) return;
    setApplyBusy(true);
    try {
      const res = await fetch(APPLICANTS_URL, { method: 'POST', body: JSON.stringify({ action: 'getApplyLink', regenerate: !!regenerate }) });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Could not get the link');
      setApplyLink(data.link);
      try { await navigator.clipboard.writeText(data.link); setApplyCopied(true); setTimeout(() => setApplyCopied(false), 2000); } catch {}
    } catch (err) { addNotification(`Apply link: ${err.message}`, 'error'); }
    finally { setApplyBusy(false); }
  };

  const handleDelete = async (applicant) => {
    if (!window.confirm(`Delete applicant "${applicant.fullName}"? This also removes their photo.`)) return;
    setDeletingId(applicant.id);
    try {
      const res = await fetch(APPLICANTS_URL, { method: 'POST', body: JSON.stringify({ action: 'deleteApplicant', id: applicant.id }) });
      const data = await res.json();
      if (data.success) {
        addNotification('Applicant deleted', 'success');
        setSelected(null);
        setApplicants(prev => prev.filter(a => a.id !== applicant.id));
      } else {
        addNotification(`Delete failed: ${data.error}`, 'error');
      }
    } catch (err) {
      addNotification(`Error: ${err.message}`, 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      {showAdd && (
        <AddApplicantPanel
          onClose={() => setShowAdd(false)}
          onSaved={load}
          addNotification={addNotification}
          recordedBy={recordedBy}
        />
      )}
      {selected && (
        <ApplicantDetailModal
          applicant={selected}
          onVevoChanged={load}
          onClose={() => setSelected(null)}
          onDelete={() => handleDelete(selected)}
          deleting={deletingId === selected.id}
          onPatch={(patch) => patchApplicant(selected.id, patch)}
          onHire={onHire ? (applicant) => { setSelected(null); onHire(applicant); } : null}
          onHireSorter={onHireSorter ? (applicant) => { setSelected(null); onHireSorter(applicant); } : null}
          onStartOnboarding={onStartOnboarding ? async (applicant, existing) => { await onStartOnboarding(applicant, existing); setSelected(null); } : null}
          onSendInfo={sendInfoRequest}
          onMarkReviewed={markInfoReviewed}
          addNotification={addNotification}
          onReplaced={(updated) => {
            setApplicants(prev => prev.map(a => a.id === updated.id ? { ...a, ...updated } : a));
            setSelected(prev => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
          }}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '.82rem', color: 'var(--hp-dim)' }}>
          {loading ? 'Loading…' : `${applicants.length} applicant${applicants.length === 1 ? '' : 's'} on file`}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}><RefreshCw size={13} /> Refresh</button>
          <button className="btn btn-secondary btn-sm" onClick={() => copyApplyLink(false)} disabled={applyBusy} title="A link for applicants who can't come in: they fill in their details and ID on their phone, and their card appears here">
            <Link2 size={13} /> {applyCopied ? 'Link copied' : 'Copy apply link'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}><UserPlus size={14} /> Record Applicant</button>
        </div>
      </div>
      {applyLink && (
        <div className="card" style={{ padding: '10px 12px', marginBottom: 16 }}>
          <div style={{ fontSize: '.74rem', color: 'var(--hp-dim)', marginBottom: 6 }}>
            Send this to an applicant who can’t come in. They add their details, licence and citizenship / passport on their phone, and their card appears below with a red dot once they’ve sent it. The same link works for everyone.
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <input readOnly value={applyLink} onFocus={e => e.target.select()} style={{ flex: '1 1 220px', minWidth: 0, fontSize: '.74rem', padding: '7px 9px', borderRadius: 6, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)' }} />
            <button className="btn btn-secondary btn-sm" onClick={async () => { try { await navigator.clipboard.writeText(applyLink); setApplyCopied(true); setTimeout(() => setApplyCopied(false), 2000); } catch {} }}>{applyCopied ? 'Copied' : 'Copy'}</button>
            <a className="btn btn-secondary btn-sm" href={`sms:?&body=${encodeURIComponent(`Hi, it's Paul from Hollypaul. Please fill in your details and ID here to start your application: ${applyLink}`)}`}><MessageCircle size={12} /> Text</a>
            <button className="btn btn-secondary btn-sm" onClick={() => copyApplyLink(true)} disabled={applyBusy} title="Retire this link and make a new one"><RotateCcw size={12} /> New link</button>
          </div>
        </div>
      )}

      {!loading && applicants.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--hp-dim)' }}>
          <UserPlus size={32} style={{ opacity: 0.4, marginBottom: 10 }} />
          <div style={{ fontSize: '.9rem', marginBottom: 4 }}>No applicants recorded yet</div>
          <div style={{ fontSize: '.78rem' }}>Tap “Record Applicant” to capture their details and photo.</div>
        </div>
      )}

      {applicants.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: 16 }}>
          {applicants.map(a => {
            const isHired = a.status === 'Hired' || !!a.hiredAt;
            const isNotHired = !isHired && a.status === 'Not hired';
            const hiredShort = a.hiredAt ? new Date(a.hiredAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' }) : '';
            const needsReview = !!(a.infoRequest && a.infoRequest.status === 'submitted' && !a.infoRequest.reviewedAt);
            return (
            <div
              key={a.id}
              className="card"
              style={{ padding: 0, overflow: 'hidden', cursor: 'pointer', border: needsReview ? '1px solid rgba(239,68,68,.6)' : isHired ? '1px solid rgba(22,163,74,.55)' : isNotHired ? '1px solid rgba(239,68,68,.45)' : undefined }}
              onClick={() => setSelected(a)}
            >
              <div style={{ position: 'relative', width: '100%', aspectRatio: '1 / 1', background: 'var(--hp-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {needsReview && (
                  <span title="Applicant returned their details — tap to review" style={{ position: 'absolute', top: 8, left: 8, zIndex: 1, width: 12, height: 12, borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 0 3px rgba(239,68,68,.25)' }} />
                )}
                {isHired && (
                  <span style={{ position: 'absolute', top: 8, right: 8, zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--hp-green)', color: '#fff', fontSize: '.64rem', fontWeight: 700, padding: '3px 7px', borderRadius: 999, boxShadow: '0 1px 4px rgba(0,0,0,.3)' }}>
                    <CheckCircle size={11} /> HIRED{hiredShort ? ` · ${hiredShort}` : ''}
                  </span>
                )}
                {isNotHired && (
                  <span style={{ position: 'absolute', top: 8, right: 8, zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--hp-red)', color: '#fff', fontSize: '.64rem', fontWeight: 700, padding: '3px 7px', borderRadius: 999, boxShadow: '0 1px 4px rgba(0,0,0,.3)' }}>
                    <XCircle size={11} /> NOT HIRED
                  </span>
                )}
                {a.photoThumb || a.photoUrl ? (
                  <img
                    src={a.photoThumb || a.photoUrl}
                    alt={a.fullName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <span style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--hp-gold)' }}>
                    {(a.fullName || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </span>
                )}
              </div>
              <div style={{ padding: '12px 14px' }}>
                <div style={{ fontWeight: 600, fontSize: '.92rem', marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.fullName || '—'}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.76rem', color: 'var(--hp-dim)', marginBottom: 3 }}>
                  <Phone size={11} /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.mobile || '—'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.76rem', color: 'var(--hp-dim)' }}>
                  <Mail size={11} /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.email || '—'}</span>
                </div>
                {a.vevo && a.vevo.check && (() => {
                  const r = VEVO_RIGHT[a.vevo.check.workRight] || VEVO_RIGHT.unknown;
                  return (
                    <div title={a.vevo.check.workBasis} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, marginRight: 6, fontSize: '.68rem', fontWeight: 700, color: r.color, background: r.bg, borderRadius: 4, padding: '2px 7px' }}>
                      <ShieldCheck size={10} /> {r.label}
                    </div>
                  );
                })()}
                {Array.isArray(a.documents) && a.documents.length > 0 && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: '.68rem', color: 'var(--hp-gold)', background: 'rgba(201,168,76,.1)', border: '1px solid rgba(201,168,76,.25)', borderRadius: 4, padding: '2px 7px' }}>
                    <FileText size={10} /> {a.documents.length} document{a.documents.length === 1 ? '' : 's'}
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default ApplicantsView;
